import React, { useCallback, useMemo } from "react";
import { useLoadScript } from "@react-google-maps/api";
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";
import {
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
  ComboboxPopover,
} from "@reach/combobox";
import "@reach/combobox/styles.css";
import InputField from "../../shared-components/InputField";
import AddButton from "../../shared-components/AddButton";
import { Addresses } from "../../api/Person";
import { XIcon } from "../../shared-components/icons";
import { uniqueUUID } from "../../utils";
import { classNames } from "../../utils";
import { toDateInputValue } from "./helpers";
import { useRowForm } from "./useRowForm";
import { PersonEditSectionModal } from "./PersonEditSectionModal";

export interface AddressFormRow {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  start_date: string;
  end_date: string;
  key: string;
  external_id?: string | null;
}

function toFormRow(addr: Addresses, key: string): AddressFormRow {
  return {
    line1: addr.line1 ?? "",
    line2: addr.line2 ?? "",
    city: addr.city ?? "",
    state: addr.state ?? "",
    zip: addr.zip ?? "",
    country: addr.country ?? "",
    start_date: toDateInputValue(addr.start_date) || "",
    end_date: toDateInputValue(addr.end_date) || "",
    key,
    external_id: addr.external_id ?? undefined,
  };
}

function toAddressPayload(row: AddressFormRow) {
  return {
    line1: row.line1.trim() || "",
    line2: row.line2.trim() || "",
    city: row.city.trim() || "",
    state: row.state.trim() || "",
    zip: row.zip.trim() || "",
    country: row.country.trim() || "",
    start_date: row.start_date.trim() || "",
    end_date: row.end_date.trim() || "",
    external_id: row.external_id ?? null,
  };
}

interface EditAddressModalProps {
  open: boolean;
  onClose: () => void;
  initialAddresses: Addresses[];
  onSave: (
    addresses: Array<ReturnType<typeof toAddressPayload>>
  ) => Promise<void>;
  onDirtyChange: (dirty: boolean) => void;
  isSaving?: boolean;
}

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GTM_KEY || "";

function parseAddressComponents(
  components: google.maps.GeocoderAddressComponent[]
) {
  let line1 = "";
  let streetNumber = "";
  let route = "";
  let city = "";
  let state = "";
  let zip = "";
  let country = "";
  components.forEach((c) => {
    if (c.types.includes("street_number")) streetNumber = c.long_name;
    else if (c.types.includes("route")) route = c.long_name;
    else if (c.types.includes("locality")) city = c.long_name;
    else if (c.types.includes("administrative_area_level_1"))
      state = c.short_name ?? "";
    else if (c.types.includes("postal_code")) zip = c.short_name ?? "";
    else if (c.types.includes("country")) country = c.short_name ?? "";
  });
  if (streetNumber && route) line1 = `${streetNumber} ${route}`;
  else if (route) line1 = route;
  else if (streetNumber) line1 = streetNumber;
  return { line1, city, state, zip, country };
}

interface StreetFieldWithPlacesProps {
  rowIndex: number;
  line1: string;
  onLine1Change: (value: string) => void;
  onFillFromPlace: (address: string) => void;
  googleAvailable: boolean;
}

function StreetFieldWithPlaces({
  rowIndex,
  line1,
  onLine1Change,
  onFillFromPlace,
  googleAvailable,
}: StreetFieldWithPlacesProps) {
  const {
    ready: placesReady,
    setValue: setPlacesValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete({ debounce: 300 });

  if (!googleAvailable) {
    return (
      <InputField
        label="Street"
        value={line1}
        handleChange={(e) => onLine1Change(e.target.value)}
        name={`addr-line1-${rowIndex}`}
        placeholder="Street address"
      />
    );
  }

  return (
    <div className="min-w-0">
      <label
        htmlFor={`addr-line1-${rowIndex}`}
        className="block text-sm font-medium text-gray-600 mb-2"
      >
        Street
      </label>
      <Combobox
        onSelect={(addr) => {
          onFillFromPlace(addr);
          clearSuggestions();
        }}
      >
        <ComboboxInput
          id={`addr-line1-${rowIndex}`}
          value={line1}
          onChange={(e) => {
            onLine1Change(e.target.value);
            setPlacesValue(e.target.value);
          }}
          disabled={!placesReady}
          placeholder="Street address (type for suggestions)"
          className="w-full p-2.5 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500"
        />
        <ComboboxPopover className="z-50">
          <ComboboxList className="border border-gray-300 rounded-lg bg-white shadow-lg max-h-48 overflow-auto">
            {status === "OK" &&
              data.map(({ place_id, description }) => (
                <ComboboxOption
                  key={place_id}
                  value={description}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-0"
                />
              ))}
          </ComboboxList>
        </ComboboxPopover>
      </Combobox>
    </div>
  );
}

const emptyAddressRow = (): AddressFormRow => ({
  line1: "",
  line2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  start_date: "",
  end_date: "",
  key: uniqueUUID(),
});

const EditAddressModal: React.FC<EditAddressModalProps> = ({
  open,
  onClose,
  initialAddresses,
  onSave,
  onDirtyChange,
  isSaving = false,
}) => {
  const initialFormRows = useMemo(
    () =>
      initialAddresses.length > 0
        ? initialAddresses.map((a) =>
            toFormRow(a, a.external_id ?? uniqueUUID())
          )
        : [],
    [initialAddresses]
  );
  const getEmptyRow = useCallback(emptyAddressRow, []);

  const { rows, setRows, updateRow, addRow, removeRow } = useRowForm(
    open,
    initialFormRows,
    getEmptyRow,
    onDirtyChange
  );

  const [libraries] = React.useState<["places"]>(["places"]);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries,
  });

  const fillRowFromPlace = useCallback(
    async (rowIndex: number, address: string) => {
      try {
        const results = await getGeocode({ address });
        if (!results[0]?.address_components) return;
        const { line1, city, state, zip, country } = parseAddressComponents(
          results[0].address_components
        );
        setRows((prev) => {
          const next = [...prev];
          if (rowIndex < 0 || rowIndex >= next.length) return prev;
          next[rowIndex] = {
            ...next[rowIndex],
            line1,
            line2: "", // clear so stale line2 from a previous address isn't saved
            city,
            state,
            zip,
            country,
          };
          return next;
        });
      } catch {
        // Geocode failure: user can still type manually.
      }
    },
    [setRows]
  );

  const handleSave = useCallback(async () => {
    const payload = rows
      .map(toAddressPayload)
      .filter((a) => a.line1 || a.city || a.state || a.zip || a.country);
    await onSave(payload.length > 0 ? payload : []);
  }, [rows, onSave]);

  return (
    <PersonEditSectionModal
      open={open}
      onClose={onClose}
      onSave={handleSave}
      isSaving={isSaving}
      saveErrorMessage="Failed to save address(es). Please try again."
    >
      <div className="pt-2">
        <div className="gap-2 p-4 border border-gray-200 rounded-lg bg-gray-100 space-y-4">
          <h3 className="text-sm font-bold text-gray-800">Address(es)</h3>
          <div className="space-y-4">
            {rows.map((row, idx) => (
              <div key={row.key} className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="min-w-0">
                    <StreetFieldWithPlaces
                      rowIndex={idx}
                      line1={row.line1}
                      onLine1Change={(value) => updateRow(idx, "line1", value)}
                      onFillFromPlace={(address) =>
                        fillRowFromPlace(idx, address)
                      }
                      googleAvailable={!!(GOOGLE_MAPS_KEY && isLoaded)}
                    />
                  </div>
                  <InputField
                    label="City"
                    value={row.city}
                    handleChange={(e) => updateRow(idx, "city", e.target.value)}
                    name={`addr-city-${idx}`}
                    classes="min-w-0 w-full"
                  />
                  <InputField
                    label="State"
                    value={row.state}
                    handleChange={(e) =>
                      updateRow(idx, "state", e.target.value)
                    }
                    name={`addr-state-${idx}`}
                    classes="min-w-0 w-full"
                  />
                </div>
                <div
                  className={classNames(
                    "grid gap-3",
                    rows.length > 1 ? "grid-cols-[1fr_1fr_auto]" : "grid-cols-2"
                  )}
                >
                  <InputField
                    label="Zip Code"
                    value={row.zip}
                    handleChange={(e) => updateRow(idx, "zip", e.target.value)}
                    name={`addr-zip-${idx}`}
                    classes="min-w-0 w-full"
                  />
                  <InputField
                    label="Country"
                    value={row.country}
                    handleChange={(e) =>
                      updateRow(idx, "country", e.target.value)
                    }
                    name={`addr-country-${idx}`}
                    placeholder="e.g. US"
                    classes="min-w-0 w-full"
                  />
                  {rows.length > 1 && (
                    <div className="flex items-end pb-1">
                      <button
                        type="button"
                        className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 border border-gray-300"
                        onClick={() => removeRow(idx)}
                        aria-label="Remove address"
                      >
                        <XIcon />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <AddButton
            text="Add Address"
            onClick={addRow}
            className="text-blue-600 hover:opacity-80 font-medium"
          />
        </div>
      </div>
    </PersonEditSectionModal>
  );
};

export default EditAddressModal;
